import { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import dayjs from 'dayjs';
import omitBy from 'lodash/omitBy';
import isNull from 'lodash/isNull';

import {
  getReturnToVendorList,
  exportReturnToVendorList
} from 'services/fulfillment-return-to-vendor';

import { downloadAsXlsx } from 'utils';
import {
  getSerialForScan,
  getSkuForScan
} from 'constants/fulfillment-sales-orders';
import {
  getRTVPathById,
  renderReturnToVendorTag,
  RETURN_TO_VENDOR_OPTIONS_IDS,
  RETURN_TO_VENDOR_SEARCH_OPTIONS,
  RTV_KEY
} from 'constants/fulfillment-return-to-vendor';
import { renderDate } from 'constants/fulfillment-common';
import { SllrWrapper } from 'contexts/sllr.context';

import { TABLE_PAGE_SIZE_OPTIONS } from 'constants/fulfillment';
import BRTable from 'components/BRTable/BRTable';
import { notify } from 'components/Notify/Notify';

const ReturnToVendorTable = ({
  intl,
  tableFilters,
  isFirstFetchDone,
  setIsFirstFetchDone
}) => {
  const { setIsLoading } = useContext(SllrWrapper);
  const [tableItemsCount, setTableItemsCount] = useState(0);
  const [selectedSearchOption, setSelectedSearchOption] = useState(
    RETURN_TO_VENDOR_OPTIONS_IDS.RTV.value
  );
  const [sharedPayload, setSharedPayload] = useState({});
  const [searchValue, setSearchValue] = useState('');

  const refreshTable = useRef();

  const columns = [
    {
      dataIndex: 'id',
      title: intl.formatMessage({
        id: 'fulfillment_common.rtv'
      }),
      render: (id) => (
        <div className="br-fulfillment-returns-table__tracking-number-col">
          <Link target="_blank" to={getRTVPathById(id)}>
            {intl.formatMessage(
              {
                id: 'fulfillment_common.rtv_id'
              },
              {
                rtvId: id
              }
            )}
          </Link>
        </div>
      )
    },
    {
      dataIndex: 'businessName',
      title: intl.formatMessage({
        id: 'fulfillment_common.business_name'
      })
    },
    {
      dataIndex: 'skuCount',
      title: intl.formatMessage({
        id: 'fulfillment_common.sku'
      })
    },
    {
      dataIndex: 'totalQuantity',
      title: intl.formatMessage({
        id: 'fulfillment_common.qty'
      })
    },
    {
      dataIndex: 'state',
      title: intl.formatMessage({
        id: 'fulfillment_common.status'
      }),
      render: renderReturnToVendorTag
    },
    {
      dataIndex: 'createdByName',
      title: intl.formatMessage({
        id: 'fulfillment_common.completed_by' // Will be created_by in phase 2
      })
    },
    {
      dataIndex: 'completedAt',
      title: intl.formatMessage({
        id: `fulfillment_common.dates.completed_at`
      }),
      render: renderDate
    }
  ];

  const handleGetTableData = async ({ page, limit, searchInputText }) => {
    setIsLoading(true);

    let value = searchInputText?.trim();
    const { RTV, SERIAL, SKU } = RETURN_TO_VENDOR_OPTIONS_IDS;
    switch (selectedSearchOption) {
      case SKU.value:
        value = getSkuForScan(value);
        break;

      case SERIAL.value:
        value = getSerialForScan(value);
        break;

      case RTV.value:
        value = value?.replace(RTV_KEY, '');
        break;

      default:
        break;
    }

    setSearchValue(value);

    const payload = omitBy(
      {
        page,
        limit,
        ...tableFilters
      },
      isNull
    );

    if (value) {
      payload[RETURN_TO_VENDOR_OPTIONS_IDS[selectedSearchOption].apiKey] =
        value;
    }

    setSharedPayload(payload);
    setIsFirstFetchDone(true);

    try {
      const {
        data: { rtvs = [], count = 0 }
      } = await getReturnToVendorList(payload);
      setTableItemsCount(count);
      setIsLoading(false);

      return {
        list: rtvs,
        total: count
      };
    } catch (error) {
      notify(error.message);
      setIsLoading(false);
    }
  };

  const handleExportData = async () => {
    setIsLoading(true);
    try {
      const data = await exportReturnToVendorList(sharedPayload);
      const fileName = `Requests_${dayjs().format('DD_MM_YYYY')}`;
      downloadAsXlsx(data, fileName);
      notify(
        intl.formatMessage({
          id: 'common.file_downloaded_successfully'
        }),
        'success'
      );
    } catch (error) {
      notify(error.message);
    }
    setIsLoading(false);
  };

  const acceptMethods = (refreshMethod) => {
    refreshTable.current = () => refreshMethod({ pageNumber: 1 });
  };

  const updateTable = () => {
    refreshTable.current();
  };

  useEffect(() => {
    if (isFirstFetchDone && refreshTable?.current) {
      updateTable();
    }
  }, [tableFilters]);

  return (
    <BRTable
      className="br-fulfillment-table br-fulfillment-problematic-jobs-table"
      title={intl.formatMessage(
        {
          id: 'fulfillment_return_to_vendor.return_to_vendor_table.title'
        },
        {
          count: tableItemsCount
        }
      )}
      columns={columns}
      listFunction={handleGetTableData}
      shareMethods={acceptMethods}
      searchPlaceholder={intl.formatMessage(
        {
          id: 'fulfillment_returns.return_orders_table.search_placeholder'
        },
        {
          searchBy: RETURN_TO_VENDOR_OPTIONS_IDS[selectedSearchOption].label
        }
      )}
      exportListFileFunction={handleExportData}
      showFilter={false}
      showSearch
      searchOptions={RETURN_TO_VENDOR_SEARCH_OPTIONS}
      selectedSearchOption={selectedSearchOption}
      setSelectedSearchOption={setSelectedSearchOption}
      pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
      emptySearchContent={{
        title: intl.formatMessage({ id: 'common.no_results_found' }),
        hideAction: !searchValue
      }}
    />
  );
};

export default injectIntl(ReturnToVendorTable);
